// modules/ads-stats/useAdsStats.ts
// Hook для управління звітом по оголошеннях

import { useState, useCallback, useEffect, useRef } from 'react';
import { api, ApiClientError } from '../../core/apiClient.ts';
import { useToast } from '../../core/contexts/ToastContext.tsx';

export type TabKey = 'ads' | 'geo' | 'accounts' | 'campaigns';

// ─── Types ─────────────────────────────────────────────────────

export interface AdsReportRow {
  ad_id: string;
  ad_name: string | null;
  ad_status: string | null;
  campaign_name: string | null;
  adset_name: string | null;
  ad_account_id: string;
  ad_account_name: string | null;
  currency: string | null;
  spend: number;
  impressions: number;
  clicks: number;
  cpm: number;
  cpc: number;
  ctr: number;
  leads: number;
  cost_per_lead: number;
  app_installs: number;
  cost_per_app_install: number;
  registrations: number;
  cost_per_registration: number;
  purchases: number;
  cost_per_purchase: number;
  campaign_budget?: number | null;
  campaign_budget_type?: string | null;
  adset_budget?: number | null;
  adset_budget_type?: string | null;
  adset_id?: string | null;
  campaign_id?: string | null;
}

export interface AdInsightSyncLog {
  id: number;
  date: string;
  accountsRequested: number | null;
  accountsSuccess: number | null;
  accountsFailed: number | null;
  durationMs: number | null;
  errorDetails: string | null;
  createdAt: string;
}

export type SortField = keyof AdsReportRow;
export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export interface DateRange {
  from: string;
  to: string;
}

export interface MetricFilter {
  field: string;
  operator: '>=' | '<=' | '=';
  value: number;
}

// ─── Date helpers ───────────────────────────────────────────────

function toYMD(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function getPresets(): Record<string, DateRange> {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const d7 = new Date(today);
  d7.setDate(today.getDate() - 7);
  const d30 = new Date(today);
  d30.setDate(today.getDate() - 30);

  return {
    today: { from: toYMD(today), to: toYMD(today) },
    yesterday: { from: toYMD(yesterday), to: toYMD(yesterday) },
    last7: { from: toYMD(d7), to: toYMD(yesterday) },
    last30: { from: toYMD(d30), to: toYMD(yesterday) },
  };
}

// ─── localStorage ───────────────────────────────────────────────

const LS_ACCOUNT_FILTER_KEY = 'adsStatsAccountFilter';
const LS_DATE_RANGE_KEY = 'adsStatsDateRange';

function loadFilterFromStorage(): string[] | null {
  try {
    const raw = localStorage.getItem(LS_ACCOUNT_FILTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch { return null; }
}

function saveFilterToStorage(ids: string[] | null): void {
  try {
    if (ids && ids.length > 0) {
      localStorage.setItem(LS_ACCOUNT_FILTER_KEY, JSON.stringify(ids));
    } else {
      localStorage.removeItem(LS_ACCOUNT_FILTER_KEY);
    }
  } catch { /* ignore */ }
}

function loadDateRangeFromStorage(): DateRange | null {
  try {
    const raw = localStorage.getItem(LS_DATE_RANGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DateRange;
  } catch { return null; }
}

function saveDateRangeToStorage(range: DateRange): void {
  try {
    localStorage.setItem(LS_DATE_RANGE_KEY, JSON.stringify(range));
  } catch { /* ignore */ }
}

const LS_STATUS_FILTER_KEY = 'adsStatsStatusFilter';
const LS_METRIC_FILTERS_KEY = 'adsStatsMetricFilters';

function loadStatusFilterFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(LS_STATUS_FILTER_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveStatusFilterToStorage(filter: string[]): void {
  try {
    localStorage.setItem(LS_STATUS_FILTER_KEY, JSON.stringify(filter));
  } catch { }
}

function loadMetricFiltersFromStorage(): MetricFilter[] {
  try {
    const raw = localStorage.getItem(LS_METRIC_FILTERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveMetricFiltersToStorage(filters: MetricFilter[]): void {
  try {
    localStorage.setItem(LS_METRIC_FILTERS_KEY, JSON.stringify(filters));
  } catch { }
}

// ─── Sorting ────────────────────────────────────────────────────

function sortRows(rows: AdsReportRow[], config: SortConfig): AdsReportRow[] {
  return [...rows].sort((a, b) => {
    const aVal = a[config.field];
    const bVal = b[config.field];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    let cmp: number;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      cmp = aVal - bVal;
    } else {
      cmp = String(aVal).localeCompare(String(bVal), 'uk');
    }
    return config.direction === 'desc' ? -cmp : cmp;
  });
}

// ─── Hook ───────────────────────────────────────────────────────

export function useAdsStats() {
  const { showToast } = useToast();

  const presets = getPresets();
  const defaultRange = loadDateRangeFromStorage() ?? presets.yesterday;

  const [dateRange, setDateRangeState] = useState<DateRange>(defaultRange);
  const [activeTab, setActiveTab] = useState<TabKey>('ads');

  const [reports, setReports] = useState<{
    ads: AdsReportRow[] | null;
    geo: any[] | null;
    accounts: any[] | null;
    campaigns: any[] | null;
  }>({
    ads: null,
    geo: null,
    accounts: null,
    campaigns: null,
  });

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<AdInsightSyncLog | null>(null);

  const [accountFilter, setAccountFilterState] = useState<string[] | null>(
    () => loadFilterFromStorage()
  );

  const setAccountFilter = useCallback((ids: string[] | null) => {
    const normalized = ids && ids.length > 0 ? ids : null;
    setAccountFilterState(normalized);
    saveFilterToStorage(normalized);
  }, []);

  const setDateRange = useCallback((range: DateRange) => {
    setDateRangeState(range);
    saveDateRangeToStorage(range);
  }, []);

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'spend',
    direction: 'desc',
  });

  const [searchQuery, setSearchQuery] = useState('');

  const [statusFilter, setStatusFilterState] = useState<string[]>(
    () => loadStatusFilterFromStorage()
  );
  const setStatusFilter = useCallback((filter: string[]) => {
    setStatusFilterState(filter);
    saveStatusFilterToStorage(filter);
  }, []);

  const [metricFilters, setMetricFiltersState] = useState<MetricFilter[]>(
    () => loadMetricFiltersFromStorage()
  );
  const setMetricFilters = useCallback((filters: MetricFilter[]) => {
    setMetricFiltersState(filters);
    saveMetricFiltersToStorage(filters);
  }, []);

  // Normalize active report for the table
  const getNormalizedActiveReport = (): AdsReportRow[] => {
    const raw = reports[activeTab];
    if (!raw) return [];
    if (activeTab === 'ads') return raw;

    return raw.map((d: any, idx: number) => {
      // ВАЖЛИВО: ad_id тут — це СИНТЕТИЧНИЙ ключ лише для React key / вибору рядка
      // в таблиці. Це НЕ справжній Facebook object id і НЕ МОЖНА відправляти
      // його в update-status/update-budget. Для реальних дій по кампанії
      // використовуй row.campaign_id (може бути null — тоді дія недоступна).
      let ad_id = '';
      let ad_name = null;
      let ad_account_id = d.ad_account_id ?? '';
      let campaign_name = d.campaign_name ?? null;

      if (activeTab === 'geo') {
        ad_id = d.country || `geo-${idx}`;
        ad_name = d.country;
      } else if (activeTab === 'accounts') {
        ad_id = d.ad_account_id ? `act_${d.ad_account_id}` : `acc-${idx}`;
        ad_name = d.ad_account_name || d.ad_account_id;
      } else if (activeTab === 'campaigns') {
        // Фолбек на назву — тільки для унікальності React key/вибору,
        // ніколи не використовувати як Facebook id для API-запитів.
        ad_id = d.campaign_id || d.campaign_name || `camp-${idx}`;
        ad_name = d.campaign_name;
      }

      return {
        ...d,
        ad_id,
        // campaign_id залишається як прийшов з бекенду (справжній FB id або null) —
        // спред `...d` вище вже це забезпечує, явно дублюємо для наочності.
        campaign_id: d.campaign_id ?? null,
        ad_name,
        ad_status: d.ad_status ?? null,
        campaign_name,
        ad_account_id,
        adset_name: null,
        ad_account_name: d.ad_account_name ?? null,
        currency: d.currency ?? null,
        leads: d.leads ?? 0,
        cost_per_lead: d.cost_per_lead ?? 0,
        app_installs: d.app_installs ?? 0,
        cost_per_app_install: d.cost_per_app_install ?? 0,
        registrations: d.registrations ?? 0,
        cost_per_registration: d.cost_per_registration ?? 0,
        purchases: d.purchases ?? 0,
        cost_per_purchase: d.cost_per_purchase ?? 0,
      } as AdsReportRow;
    });
  };
  const activeReportNormalized = getNormalizedActiveReport();

  // Apply filters
  const filteredReport = activeReportNormalized.filter((row) => {
    // 1. Search Query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        (row.campaign_name?.toLowerCase().includes(q)) ||
        (row.ad_name?.toLowerCase().includes(q)) ||
        (row.ad_account_id?.toLowerCase().includes(q));
      if (!matchesSearch) return false;
    }

    // 2. Status Filter
    if (statusFilter.length > 0) {
      if (!row.ad_status || !statusFilter.includes(row.ad_status)) {
        return false;
      }
    }

    // 3. Metric Filters
    if (metricFilters.length > 0) {
      for (const filter of metricFilters) {
        const rowVal = Number(row[filter.field as keyof AdsReportRow] ?? 0);
        if (filter.operator === '>=') {
          if (rowVal < filter.value) return false;
        } else if (filter.operator === '<=') {
          if (rowVal > filter.value) return false;
        } else if (filter.operator === '=') {
          if (rowVal !== filter.value) return false;
        }
      }
    }

    return true;
  });

  const sortedReport = sortRows(filteredReport, sortConfig);

  // ── Fetch report ─────────────────────────────────────────────

  const fetchReport = useCallback(
    async (range: DateRange, accIds: string[] | null, tab: TabKey) => {
      setLoading(true);
      try {
        let endpoint = '/api/ads-stats/report/ads';
        if (tab === 'geo') endpoint = '/api/ads-stats/report/geo';
        if (tab === 'accounts') endpoint = '/api/ads-stats/report/accounts';
        if (tab === 'campaigns') endpoint = '/api/ads-stats/report/campaigns';

        let url = `${endpoint}?date_from=${encodeURIComponent(range.from)}&date_to=${encodeURIComponent(range.to)}`;
        if (accIds && accIds.length > 0) {
          url += `&account_ids=${encodeURIComponent(accIds.join(','))}`;
        }
        const data = await api.get<any[]>(url);
        setReports(prev => ({ ...prev, [tab]: data }));
        return data;
      } catch (err) {
        const msg = err instanceof ApiClientError ? err.message : 'Не вдалося завантажити звіт';
        showToast(msg, 'error');
        setReports(prev => ({ ...prev, [tab]: [] }));
        return [];
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  // ── Fetch last sync log ───────────────────────────────────────

  const fetchLastSync = useCallback(async (range: DateRange) => {
    try {
      // Для діапазону показуємо лог по першій даті (або можна по будь-якій)
      const dateKey = range.from === range.to ? range.from : `${range.from}..${range.to}`;
      const log = await api.get<AdInsightSyncLog | null>(
        `/api/ads-stats/sync-logs/last?date=${encodeURIComponent(dateKey)}`
      );
      setLastSync(log);
    } catch {
      setLastSync(null);
    }
  }, []);

  // ── Auto-load on range/filter change ─────────────────────────

  const prevRangeRef = useRef<string>('');
  const prevFilterRef = useRef<string | null>(null);

  useEffect(() => {
    const rangeKey = `${dateRange.from}..${dateRange.to}`;
    const filterKey = accountFilter ? accountFilter.join(',') : null;

    // Якщо змінились фільтри - скидаємо всі кеші і фетчимо активний таб
    if (rangeKey !== prevRangeRef.current || filterKey !== prevFilterRef.current) {
      prevRangeRef.current = rangeKey;
      prevFilterRef.current = filterKey;

      setReports({ ads: null, geo: null, accounts: null, campaigns: null });
      fetchReport(dateRange, accountFilter, activeTab);
      fetchLastSync(dateRange);
    } else {
      // Якщо фільтри ті самі, але змінився таб, перевіряємо чи є він в кеші
      if (reports[activeTab] === null && !loading) {
        fetchReport(dateRange, accountFilter, activeTab);
      }
    }
  }, [dateRange, accountFilter, activeTab, fetchReport, fetchLastSync, reports, loading]);

  const fetchAllReports = async () => {
    const toFetch = [];
    if (reports.ads === null) toFetch.push(fetchReport(dateRange, accountFilter, 'ads'));
    if (reports.geo === null) toFetch.push(fetchReport(dateRange, accountFilter, 'geo'));
    if (reports.accounts === null) toFetch.push(fetchReport(dateRange, accountFilter, 'accounts'));
    if (reports.campaigns === null) toFetch.push(fetchReport(dateRange, accountFilter, 'campaigns'));
    await Promise.all(toFetch);

    // return fresh snapshot
    return {
      ads: reports.ads ?? await fetchReport(dateRange, accountFilter, 'ads'),
      geo: reports.geo ?? await fetchReport(dateRange, accountFilter, 'geo'),
      accounts: reports.accounts ?? await fetchReport(dateRange, accountFilter, 'accounts'),
      campaigns: reports.campaigns ?? await fetchReport(dateRange, accountFilter, 'campaigns')
    };
  };

  // ── Sync ─────────────────────────────────────────────────────

  const sync = useCallback(async () => {
    setSyncing(true);
    const startTime = Date.now();
    try {
      const body: { date_from: string; date_to: string; account_ids?: string[] } = {
        date_from: dateRange.from,
        date_to: dateRange.to,
      };
      if (accountFilter && accountFilter.length > 0) {
        body.account_ids = accountFilter;
      }

      showToast('Запуск синхронізації...', 'info');

      // 1. Start Sync
      const startResult = await api.post<{
        ok: boolean;
        jobs: { accountId: string; runId: string }[];
        accountsRequested: number;
        errors?: Array<{ ad_account_id: string; error: string }>;
      }>('/api/ads-stats/sync/start', body);

      if (!startResult.ok || !startResult.jobs.length) {
        showToast('Помилка запуску: немає кабінетів для синхронізації', 'error');
        setSyncing(false);
        return;
      }

      let pendingJobs = startResult.jobs;
      let completedCount = 0;
      let failedCount = startResult.errors?.length || 0;
      const allErrors = startResult.errors ? [...startResult.errors] : [];

      // 2. Poll with exponential backoff
      let pollInterval = 5000;
      const maxInterval = 20000;

      while (pendingJobs.length > 0) {
        await new Promise(r => setTimeout(r, pollInterval));
        pollInterval = Math.min(pollInterval + 5000, maxInterval);

        const pollResult = await api.post<{
          stillPending: { accountId: string; runId: string }[];
          completed: string[];
          failed: { accountId: string; error: string }[];
        }>('/api/ads-stats/sync/poll', {
          date_from: dateRange.from,
          date_to: dateRange.to,
          jobs: pendingJobs
        });

        pendingJobs = pollResult.stillPending;
        completedCount += pollResult.completed.length;
        failedCount += pollResult.failed.length;
        pollResult.failed.forEach(f => allErrors.push({ ad_account_id: f.accountId, error: f.error }));
      }

      // 3. Log
      await api.post('/api/ads-stats/sync/log', {
        date_from: dateRange.from,
        date_to: dateRange.to,
        accountsRequested: startResult.accountsRequested,
        accountsSuccess: completedCount,
        accountsFailed: failedCount,
        durationMs: Date.now() - startTime,
        errors: allErrors
      });

      if (completedCount > 0) {
        showToast(
          `Синхронізовано: ${completedCount} кабінетів, ${failedCount} помилок`,
          failedCount > 0 ? 'info' : 'success'
        );
      } else {
        showToast('Помилка синхронізації: всі кабінети провалились', 'error');
      }

      await fetchReport(dateRange, accountFilter, activeTab);
      await fetchLastSync(dateRange);
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'Помилка синхронізації';
      showToast(msg, 'error');
    } finally {
      setSyncing(false);
    }
  }, [dateRange, accountFilter, showToast, fetchReport, fetchLastSync, activeTab]);

  // ── Toggle sort ───────────────────────────────────────────────

  const toggleSort = useCallback((field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  /**
   * Оптимістично оновити рядок(и) у вже завантаженому звіті (без мережевих
   * запитів). Використовується після успішного update-status/update-budget —
   * бекенд вже підтвердив зміну на Facebook, тож немає сенсу тягнути
   * ПОВНУ синхронізацію всіх кабінетів (sync()) заради однієї дії.
   *
   * Оновлює УСІ рядки з matchField === matchValue (наприклад, кілька ads
   * можуть належати одному adset_id — бюджет зміниться для всіх одразу).
   *
   * @param tab        — вкладка звіту ('ads' | 'campaigns' | ...)
   * @param matchField  — поле для пошуку рядка (напр. 'ad_id', 'campaign_id', 'adset_id')
   * @param matchValue — значення справжнього Facebook id
   * @param patch      — поля для часткового оновлення (напр. { ad_status: 'PAUSED' })
   */
  const patchReportRow = useCallback((tab: TabKey, matchField: string, matchValue: string, patch: Record<string, any>) => {
    setReports((prev) => {
      const list = prev[tab];
      if (!list) return prev;
      const next = list.map((row: any) => (row[matchField] === matchValue ? { ...row, ...patch } : row));
      return { ...prev, [tab]: next };
    });
  }, []);

  return {
    activeTab,
    setActiveTab,
    report: sortedReport,
    reports,
    fetchAllReports,
    loading,
    syncing,
    lastSync,
    dateRange,
    setDateRange,
    sync,
    patchReportRow,
    sortConfig,
    toggleSort,
    accountFilter,
    setAccountFilter,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    metricFilters,
    setMetricFilters,
  };
}